import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyIrcamMode({ brand, today, Copy }: ModeRendererProps) {
  // IRCAM gradient: sonic research spectrum
  const ircamGradient = (
    <defs>
      <linearGradient id="ircamGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D32F2F" />
        <stop offset="50%" stopColor="#7B1FA2" />
        <stop offset="100%" stopColor="#303F9F" />
      </linearGradient>
    </defs>
  );

  // Spectral tilings: frequency analysis
  const spectralTilings: JSX.Element[] = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const freq = row / 16;
      const amplitude = Math.sin(col * 0.6 + row * 0.4) * 0.5 + 0.5;
      const size = 12 + amplitude * 20;
      const color = freq < 0.33 ? "#D32F2F" : freq < 0.66 ? "#7B1FA2" : "#303F9F";
      spectralTilings.push(
        <rect
          key={`spec-${row}-${col}`}
          x={x - size / 2}
          y={y - size / 2}
          width={size}
          height={size}
          fill={color}
          opacity={0.3 + amplitude * 0.4}
          rx={3}
        />
      );
    }
  }

  // Pitch lattices: harmonic series
  const pitchLattices: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const y = 140 + i * 75;
    const fundamental = 100;
    for (let j = 1; j <= 8; j++) {
      const x = 100 + j * 75;
      const radius = 8 + j * 2;
      pitchLattices.push(
        <circle
          key={`pitch-${i}-${j}`}
          cx={x}
          cy={y}
          r={radius}
          fill="none"
          stroke="url(#ircamGrad)"
          strokeWidth={1.5}
          opacity={0.4}
        />
      );
    }
  }

  // Rhythmic tessellation: temporal patterns
  const rhythmicTessellation: JSX.Element[] = [];
  for (let i = 0; i < 24; i++) {
    const angle = (i * Math.PI * 2) / 24;
    const radius = 200;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    const beat = i % 3 === 0;
    rhythmicTessellation.push(
      <g key={`rhythm-${i}`}>
        <line
          x1={397}
          y1={561.5}
          x2={x}
          y2={y}
          stroke="url(#ircamGrad)"
          strokeWidth={beat ? 2 : 1}
          opacity={beat ? 0.5 : 0.2}
        />
        <circle
          cx={x}
          cy={y}
          r={beat ? 10 : 6}
          fill="url(#ircamGrad)"
          opacity={0.6}
        />
      </g>
    );
  }

  // Spectrogram waves
  const spectrogramWaves: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const y = 100 + i * 58;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const energy = Math.sin(j * 0.7 + i * 0.3) * 30;
      points.push(`${x},${y + energy}`);
    }
    spectrogramWaves.push(
      <polyline
        key={`spectro-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#ircamGrad)"
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Central acoustic core
  const acousticCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="none" stroke="#303F9F" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#7B1FA2" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#D32F2F" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={20}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        IRCAM
      </text>
    </g>
  );

  return (
    <g>
      {ircamGradient}
      {spectrogramWaves}
      {spectralTilings}
      {pitchLattices}
      {rhythmicTessellation}
      {acousticCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
